import {Component} from '@angular/core';
import {ActionsSubject, Store} from '@ngrx/store';
import {MAIL_REGEX, UiWidget, WidgetConfiguration} from '@pt/pt-models';
import {AbstractWidgetDirective} from '../../abstract-widget.directive';
import {WidgetContactChooserV0Utils} from './widget-contact-chooser-v0.utils';
import {WidgetContactChooserValueV0Dto} from './models/widget-contact-chooser-value-v0.dto';
import {WidgetHolderChooserPreviewDataV0} from './models/widget-contact-chooser-preview-v0.dto';
import {TranslateService} from '@ngx-translate/core';
import {WidgetDialogService} from '../../../services/widget-dialog.service';
import {WidgetContactChooserArgsV0Dto} from './models/widget-contact-chooser-args-v0.dto';
import {forkJoin} from 'rxjs';
import {WidgetContactChooserDataV0} from './models/widget-contact-chooser-data-v0';
import {WidgetUtils} from '../../widget.utils';
import {NgxPermissionsService} from 'ngx-permissions';
import {QueryService} from '@pt/api-client';

@Component({
  selector: 'pt-widget-contact-chooser-v0',
  template: `
    <div class='position-relative' *ngIf="internalStatus.enabled">
      <pt-widget-disabled
        *ngIf='internalStatus.isDisabled'
        [showLoader]="internalStatus.viewMode === 'LOADING'">
      </pt-widget-disabled>

      <pt-widget-header
        class='w-100'
        [label]='widget?.label'
        [help]='widget?.help'
        [comment]="comment"
        [unsavedComment]="unsavedComment"

        [showCancelButton]='internalStatus.showCancelButton'
        [showCommentButton]='internalStatus.showCommentButton'
        [showEditButton]='internalStatus.showEditButton'
        [showReloadButton]='internalStatus.showReloadButton'
        [showSaveButton]='internalStatus.showSaveButton'
        [showOptButton]='internalStatus.showOptButton'

        [disabledCancelButton]='internalStatus.disabledCancelButton'
        [disabledCommentButton]='internalStatus.disabledCommentButton'
        [disabledEditButton]='internalStatus.disabledEditButton'
        [disabledOptButton]='internalStatus.disabledOptButton'
        [disabledReloadButton]='internalStatus.disabledReloadButton'
        [disabledSaveButton]='internalStatus.disabledSaveButton'
        [loading]='internalStatus.isLoading'

        (clickSaveWidget)='saveWidget(false)'
        (clickResetWidget)='resetWidget(widget)'
        (clickEditWidget)='dispatchEditState()'
        (clickReloadWidget)='reloadWidget(widget)'
        (clickCreateOpt)='createOptWidget()'
        (clickCommentWidget)='clickCommentWidget()'>
      </pt-widget-header>

      <ng-container *ngIf='!internalStatus.showOptButton'>

        <pt-widget-contact-chooser-v0-edit
          [emailOptional]="emailOptional"
          [show]="internalStatus.viewMode === 'EDIT'"
          [data]='data'
          (changeValue)='changeFormValue($event)'>
        </pt-widget-contact-chooser-v0-edit>

        <pt-widget-contact-chooser-v0-preview
          *ngIf="internalStatus.viewMode === 'PREVIEW' || internalStatus.viewMode === 'LOADING' "
          [noDataLabel]="noDataLabel"
          [emailOptional]="emailOptional"
          [loading]='internalStatus.isLoading'
          [previewData]='previewData'>
        </pt-widget-contact-chooser-v0-preview>

        <pt-widget-error
          *ngIf="internalStatus.viewMode === 'ERROR'"
          [errorMessage]='internalStatus.errorMessage | async'>
        </pt-widget-error>

        <pt-widget-footer></pt-widget-footer>

      </ng-container>
    </div>

  `
})
export class WidgetContactChooserV0Component extends AbstractWidgetDirective<WidgetHolderChooserPreviewDataV0> {
  currentValue: WidgetContactChooserValueV0Dto[];
  show: boolean;
  data: WidgetContactChooserDataV0;
  public emailOptional = false;

  constructor(public store: Store,
              public actionsSubject: ActionsSubject,
              public translateService: TranslateService,
              public widgetDialogService: WidgetDialogService,
              public service: QueryService,
              public permissionService: NgxPermissionsService) {
    super(store, actionsSubject, translateService, widgetDialogService, permissionService);
  }

  loadWidget(widget: UiWidget): void {
    this.show = false;
    this.data = null;
    this.dispatchInitState();

    const args: WidgetContactChooserArgsV0Dto = this.widget.args as WidgetContactChooserArgsV0Dto;
    const query1 = args.query1;
    const query2 = args.query2;
    const query3 = args.query3;
    const mapping1 = args.query1.mapping;
    const mapping2 = args.query2.mapping;
    const mapping3 = args.query3.mapping;

    const value = (this.widget.value as WidgetContactChooserValueV0Dto[]);
    const header = args.header as WidgetContactChooserValueV0Dto;
    forkJoin({
      query1: this.service.resolve(query1),
      query2: this.service.resolve(query2),
      query3: this.service.resolve(query3)
    }).subscribe(
      (responses: { query1, query2, query3 }) => {
        const col1 = WidgetContactChooserV0Utils.queryResponseToContactChooserResponse(responses.query1, mapping1, 'PLACEHOLDERS.SELECT');
        const col2 = WidgetContactChooserV0Utils.queryResponseToContactChooserResponse(responses.query2, mapping2, 'PLACEHOLDERS.SELECT');
        const col3 = WidgetContactChooserV0Utils.queryResponseToContactChooserResponse(responses.query3, mapping3, 'PLACEHOLDERS.SELECT');
        const preview = WidgetContactChooserV0Utils.generatePreview(col1, col2, col3, value, header);
        this.data = {
          header,
          col1,
          col2,
          col3,
          value
        } as WidgetContactChooserDataV0;

        this.currentValue = value;
        const validators: Array<{ key: string }> | undefined = this.widget?.validators
        this.emailOptional = !!validators && Array.isArray(validators) && validators.length === 1 && validators[0].key === 'emailOptional';

        this.valueController(widget, this.data);
        this.checkValidity();
        this.dispatchCompleteState(preview);
        this.show = true;
      },
      () => {
        this.isValid$.next(false);
        this.dispatchErrorState();
      });

  }

  saveWidget(ignoreTrigger?: boolean): void {
    const widget = JSON.parse(JSON.stringify(this.widget)) as UiWidget;
    widget.value = this.currentValue;
    widget.comment = this.comment;
    this.checkValidity();
    this.completeSaveWidget(widget, ignoreTrigger);
  }

  configuration(): WidgetConfiguration {
    return {supportAutoSave: false, supportValidation: true};
  }

  changeFormValue($event: WidgetContactChooserValueV0Dto[]) {
    this.currentValue = $event?.filter((row: WidgetContactChooserValueV0Dto) => {
      return row?.col1 !== null || row?.col2 !== null || row?.col3 !== null || row?.col4?.match(MAIL_REGEX);
    });
    this.checkValidity();
    // @giovanni Per supportare l'autosave decommentare queste righe
    // if (this.internalStatus.isAutoSave && !this.internalStatus.isLoading) {
    //   this.saveWidget();
    // }
  }

  private checkValidity() {
    switch (true) {
      case this.widget?.opt:
        this.isValid$.next(true);
        break;

      case  this.currentValue?.length === 0:
      case !!this.currentValue?.find(c => c.col1 === null || c.col1 === ''):
      case !!this.currentValue?.find(c => WidgetUtils.isInvalidMail(c.col4)) && !this.emailOptional:
        this.isValid$.next(false);
        break;

      default:
        this.isValid$.next(true);
    }
  }


  private valueController(widget: UiWidget, data: WidgetContactChooserDataV0) {
    const noData =
      !data ||
      !(data?.col1?.length > 0) ||
      !(data?.col2?.length > 0) ||
      !(data?.col3?.length > 0);

    if (noData) {
      this.sendWidgetNotificationInvalidQueryResponseNoData(data);
    }

    const widgetValue: WidgetContactChooserValueV0Dto[] = widget?.value ? widget?.value : [];
    const validValue = WidgetContactChooserV0Utils.getValidValue(widgetValue, data);
    if (validValue?.length !== widgetValue?.length) {
      const updatedWidget = JSON.parse(JSON.stringify(widget));
      updatedWidget.value = validValue;
      this.sendWidgetNotificationInvalidQueryResponseInvalidValue(updatedWidget);
    }
  }
}










